import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../../../config/URL";
import {
  createTheme,
  ThemeProvider,
  Avatar,
  Box,
  Chip,
  Stack,
  Typography,
} from "@mui/material";
import { MaterialReactTable } from "material-react-table";
import {
  getColumnPreference,
  saveColumnPreference,
} from "../../../utility/mrtColumnPersistence";

const OtherMessages = ({ selectedCenter }) => {
  const MODULE_KEY = "OTHERMESSAGE";

  const navigate = useNavigate();

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  // Pagination state (SERVER SIDE)
  const [pageIndex, setPageIndex] = useState(0); // 0-based
  const [pageSize, setPageSize] = useState(5); // match UI default
  const [rowCount, setRowCount] = useState(0);

  /* ---------------- SAFE VALUES ---------------- */
  const safePageIndex = Number.isInteger(pageIndex) ? pageIndex : 0;
  const safePageSize = Number.isInteger(pageSize) ? pageSize : 5;
  const safeRowCount = Number.isInteger(rowCount) ? rowCount : 0;

  const defaultColumnVisibility = {
    // participants: false,
    // lastMessagePreview: false,
    // lastMessageAt: false,
    createdBy: false,
    createdAt: false,
    updatedBy: false,
    updatedAt: false,
  };

  const [columnVisibility, setColumnVisibility] = useState(() =>
    getColumnPreference(MODULE_KEY, defaultColumnVisibility),
  );

  /* ---------------- FETCH DATA ---------------- */
  const getData = async () => {
    setLoading(true);
    try {
      const response = await api.get(
        `/admin/conversations?page=${safePageIndex + 1}&size=${safePageSize}`,
      );

      const apiData = response?.data?.data || [];

      const formatted = apiData.map((item) => ({
        conversationId: item.conversationId,
        title: item.title || "",
        participants: Array.isArray(item.participants) ? item.participants : [],
        lastMessagePreview: item.lastMessagePreview || "",
        lastMessageAt: item.lastMessageAt
          ? new Date(item.lastMessageAt)
              .toLocaleString("en-IN", {
                day: "2-digit",
                month: "short",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
                hour12: true,
              })
              .toUpperCase()
          : "-",
      }));

      setData(formatted);
      setRowCount(response?.data?.totalElements ?? 0);
    } catch (error) {
      console.error("Conversation fetch error:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getData();
  }, [safePageIndex, safePageSize]);

  /* ---------------- COLUMNS ---------------- */
  const columns = useMemo(
    () => [
      {
        accessorFn: (_, index) => index + 1 + safePageIndex * safePageSize,
        header: "S.NO",
        size: 60,
      },
      // {
      //   accessorKey: "title",
      //   header: "Title",
      //   Cell: ({ cell }) => cell.getValue() || "-",
      // },
      {
        accessorKey: "participants",
        header: "Participants",
        Cell: ({ cell }) => {
          const participants = cell.getValue();

          if (!participants || participants.length === 0) {
            return <Typography color="text.secondary">-</Typography>;
          }

          return (
            <Stack spacing={1}>
              {participants.map((p) => (
                <Box
                  key={p.participantId}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    backgroundColor: "#f8fafc",
                    borderRadius: 1,
                    p: "6px 10px",
                  }}
                >
                  <Avatar
                    src={p.userProfile || ""}
                    sx={{
                      width: 34,
                      height: 34,
                      bgcolor: "#287f71",
                      fontSize: 14,
                    }}
                  >
                    {!p.userProfile && p.userName?.charAt(0)}
                  </Avatar>

                  <Box>
                    <Typography fontSize={14} fontWeight={500}>
                      {p.userName}
                    </Typography>
                    <Chip
                      label={p.userType}
                      size="small"
                      sx={{
                        height: 18,
                        fontSize: "11px",
                        backgroundColor: "#e6edf7",
                        color: "#287f71",
                      }}
                    />
                  </Box>
                </Box>
              ))}
            </Stack>
          );
        },
      },
      {
        accessorKey: "lastMessagePreview",
        header: "Last Message",
        size: 260,
        Cell: ({ row }) => (
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography fontSize={13} color="text.secondary" noWrap>
              {row.original.lastMessagePreview}
            </Typography>

            {row.original.unreadCount > 0 && (
              <Chip
                label={row.original.unreadCount}
                color="error"
                size="small"
                sx={{ height: 20, fontSize: "11px" }}
              />
            )}
          </Stack>
        ),
      },
      {
        accessorKey: "lastMessageAt",
        header: "Last Activity",
        size: 180,
      },
      // { accessorKey: "createdBy", header: "Created By" },
      // {
      //   accessorKey: "createdAt",
      //   header: "Created On",
      //   Cell: ({ cell }) => cell.getValue()?.substring(0, 10),
      // },
      // {
      //   accessorKey: "updatedAt",
      //   header: "Updated On",
      //   Cell: ({ cell }) => cell.getValue()?.substring(0, 10) || "",
      // },
      // {
      //   accessorKey: "updatedBy",
      //   header: "Updated By",
      //   Cell: ({ cell }) => cell.getValue() || "",
      // },
    ],
    [safePageIndex, safePageSize],
  );

  /* ---------------- THEME ---------------- */
  const theme = createTheme({
    components: {
      MuiTableCell: {
        styleOverrides: {
          head: {
            color: "#535454",
            backgroundColor: "#e6edf7",
            fontWeight: 400,
            fontSize: "13px",
            textAlign: "center",
          },
        },
      },
    },
  });

  /* ---------------- ROW CLICK ---------------- */
  const handleRowClick = (row) => {
    const conversationId = row?.original?.conversationId;
    if (!conversationId) return;

    navigate(`/othermessaging/view/${conversationId}`);
  };

  return (
    <div className="container-fluid my-3">
      <ol className="breadcrumb my-3 px-2">
        <li>
          <Link to="/" className="custom-breadcrumb">
            Home
          </Link>
          <span className="breadcrumb-separator"> &gt; </span>
        </li>
        <li>
          Messaging
          <span className="breadcrumb-separator"> &gt; </span>
        </li>
        <li className="breadcrumb-item active">Other Messages</li>
      </ol>

      <div className="card">
        <div
          className="mb-3 d-flex justify-content-between align-items-center p-2"
          style={{ background: "#f5f7f9" }}
        >
          <span className="text-muted">
            This database shows the list of{" "}
            <strong style={{ color: "#287f71" }}>Conversations</strong>
          </span>
        </div>

        <ThemeProvider theme={theme}>
          <MaterialReactTable
            columns={columns}
            data={data}
            manualPagination
            rowCount={safeRowCount}
            state={{
              isLoading: loading,
              pagination: {
                pageIndex: safePageIndex,
                pageSize: safePageSize,
              },
              columnVisibility,
            }}
            onColumnVisibilityChange={(updater) => {
              const newVisibility =
                typeof updater === "function"
                  ? updater(columnVisibility)
                  : updater;

              setColumnVisibility(newVisibility);
              saveColumnPreference(MODULE_KEY, newVisibility);
            }}
            onPaginationChange={(updater) => {
              const next =
                typeof updater === "function"
                  ? updater({
                      pageIndex: safePageIndex,
                      pageSize: safePageSize,
                    })
                  : updater;

              // reset page index if page size changes
              if (next.pageSize !== safePageSize) {
                setPageIndex(0);
              } else {
                setPageIndex(next.pageIndex);
              }

              setPageSize(next.pageSize);
            }}
            enableColumnActions={true}
            enableColumnFilters={false}
            enableDensityToggle={false}
            enableFullScreenToggle={false}
            muiTableBodyRowProps={({ row }) => ({
              onClick: () => handleRowClick(row),
              sx: { cursor: "pointer" },
            })}
          />
        </ThemeProvider>
      </div>
    </div>
  );
};

export default OtherMessages;
